import type { Employee } from '../model/employeeSchema';

/**
 * GET /api/employees
 * GET /api/employees/:id
 *
 * Бэкенд возвращает: id, name, position, department, unit, tab_number,
 * workplace_id, work_condition_class, active, hire_date (ISO),
 * вложенные объекты medical/training/internship/sout/risk_assessment/ppe.
 * initials — вычисляется бэком из name.
 * Даты в ISO 8601. Статусы (ok/warn/err/info) вычисляются бэком
 * по дате следующего осмотра/обучения. Иконки и цвета — фронтовая логика.
 */
export const employees: Employee[] = [
  {
    id: 1,
    name: 'Петров Александр Васильевич',
    initials: 'ПА',
    position: 'Слесарь-ремонтник 5 разр.',
    department: 'Цех №3',
    unit: 'Участок №2',
    tabNumber: '00345',
    workplaceId: 'РМ-047',
    workConditionClass: '3.1',
    active: true,
    hireDate: '12.03.2019',
    medical: { status: 'err', lastDate: '15.03.2025', nextDate: '15.03.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '01.10.2025',
      nextDate: '01.04.2026',
      certNumber: '—',
      certExpiry: '—',
    },
    internship: { status: 'done', mentor: 'Баринов К.Л.', endDate: '25.03.2026', admitted: true },
    sout: { class: '3.1', date: '01.06.2021', nextDate: '01.06.2026', status: 'warn' },
    riskAssessment: { level: 'Средний', score: 8, status: 'warn', plan: '2 мероприятия в работе' },
    ppe: {
      count: 5,
      autoAssigned: true,
      items: ['Каска защитная', 'Перчатки х/б', 'Очки защитные', 'Ботинки кожаные', 'Жилет сигнальный'],
    },
  },
  {
    id: 2,
    name: 'Кузнецов Роман Николаевич',
    initials: 'КР',
    position: 'Электросварщик 5 разр.',
    department: 'Цех №1',
    unit: 'Участок №1',
    tabNumber: '00712',
    workplaceId: 'РМ-001',
    workConditionClass: '3.2',
    active: true,
    hireDate: '05.09.2017',
    medical: { status: 'err', lastDate: '20.03.2025', nextDate: '20.03.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '01.11.2025',
      nextDate: '01.05.2026',
      certNumber: 'ОТ-2025-0341',
      certExpiry: '15.01.2026',
    },
    internship: { status: 'done', mentor: 'Алексеев К.С.', endDate: '10.01.2025', admitted: true },
    sout: { class: '3.2', date: '15.03.2021', nextDate: '15.03.2026', status: 'err' },
    riskAssessment: { level: 'Высокий', score: 12, status: 'err', plan: '4 мероприятия, 1 просрочено' },
    ppe: {
      count: 8,
      autoAssigned: true,
      items: [
        'Щиток сварщика',
        'Перчатки сварщика',
        'Костюм',
        'Респиратор',
        'Ботинки кожаные',
        'Очки защитные',
        'Наушники',
        'Фартук',
      ],
    },
  },
  {
    id: 3,
    name: 'Иванов Сергей Дмитриевич',
    initials: 'ИС',
    position: 'Токарь 4 разряда',
    department: 'Цех №2',
    unit: 'Участок №3',
    tabNumber: '00489',
    workplaceId: 'РМ-023',
    workConditionClass: '3.1',
    active: true,
    hireDate: '20.07.2020',
    medical: { status: 'err', lastDate: '10.04.2025', nextDate: '10.04.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '15.10.2025',
      nextDate: '15.04.2026',
      certNumber: '—',
      certExpiry: '—',
    },
    internship: { status: 'done', mentor: 'Соколов Д.А.', endDate: '05.03.2024', admitted: true },
    sout: { class: '3.1', date: '10.06.2022', nextDate: '10.06.2026', status: 'ok' },
    riskAssessment: { level: 'Средний', score: 9, status: 'warn', plan: '1 мероприятие запланировано' },
    ppe: {
      count: 6,
      autoAssigned: true,
      items: ['Очки защитные', 'Перчатки х/б', 'Ботинки', 'Костюм х/б', 'Беруши', 'Фартук'],
    },
  },
  {
    id: 4,
    name: 'Смирнова Елена Андреевна',
    initials: 'СЕ',
    position: 'Лаборант',
    department: 'ОТК',
    unit: 'Химлаборатория',
    tabNumber: '00521',
    workplaceId: 'РМ-058',
    workConditionClass: '2',
    active: true,
    hireDate: '15.04.2026',
    medical: { status: 'info', lastDate: '—', nextDate: '05.05.2026', result: '—' },
    training: { briefingStatus: 'info', lastDate: '20.04.2026', nextDate: '—', certNumber: '—', certExpiry: '—' },
    internship: { status: 'active', mentor: 'Зимина О.Р.', endDate: '09.05.2026', admitted: false },
    sout: { class: '2', date: '12.09.2023', nextDate: '12.09.2026', status: 'ok' },
    riskAssessment: { level: 'Низкий', score: 3, status: 'ok', plan: 'Нет активных мероприятий' },
    ppe: { count: 4, autoAssigned: true, items: ['Перчатки резиновые', 'Очки химзащиты', 'Халат', 'Респиратор'] },
  },
  {
    id: 5,
    name: 'Николаева Ольга Петровна',
    initials: 'НО',
    position: 'Инженер-технолог',
    department: 'ОГТ',
    unit: 'Техбюро',
    tabNumber: '00288',
    workplaceId: 'РМ-091',
    workConditionClass: '1',
    active: true,
    hireDate: '03.02.2021',
    medical: { status: 'warn', lastDate: '15.04.2025', nextDate: '15.04.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '20.04.2026',
      nextDate: '20.10.2026',
      certNumber: 'ОТ-2025-0199',
      certExpiry: '20.04.2026',
    },
    internship: { status: 'done', mentor: '—', endDate: '—', admitted: true },
    sout: { class: '1', date: '01.03.2024', nextDate: '01.03.2026', status: 'ok' },
    riskAssessment: { level: 'Низкий', score: 2, status: 'ok', plan: 'Нет активных мероприятий' },
    ppe: { count: 2, autoAssigned: true, items: ['Перчатки х/б', 'Каска'] },
  },
  {
    id: 6,
    name: 'Федоров Виктор Михайлович',
    initials: 'ФВ',
    position: 'Крановщик',
    department: 'Цех №1',
    unit: 'Участок №2',
    tabNumber: '00634',
    workplaceId: 'РМ-012',
    workConditionClass: '3.1',
    active: true,
    hireDate: '11.06.2018',
    medical: { status: 'warn', lastDate: '20.04.2025', nextDate: '20.04.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '15.01.2026',
      nextDate: '15.04.2026',
      certNumber: 'ПТМ-2025-0078',
      certExpiry: '15.01.2026',
    },
    internship: { status: 'done', mentor: 'Баранов Г.В.', endDate: '12.06.2023', admitted: true },
    sout: { class: '3.1', date: '20.04.2022', nextDate: '20.04.2026', status: 'ok' },
    riskAssessment: { level: 'Критический', score: 10, status: 'err', plan: 'Согласование завершается' },
    ppe: {
      count: 7,
      autoAssigned: true,
      items: ['Каска', 'Жилет сигнальный', 'Перчатки', 'Ботинки', 'Страховочная привязь', 'Наушники', 'Очки'],
    },
  },
  {
    id: 7,
    name: 'Соколов Дмитрий Алексеевич',
    initials: 'СД',
    position: 'Фрезеровщик 4 разр.',
    department: 'Цех №2',
    unit: 'Участок №1',
    tabNumber: '00417',
    workplaceId: 'РМ-084',
    workConditionClass: '2',
    active: true,
    hireDate: '08.11.2019',
    medical: { status: 'ok', lastDate: '01.12.2025', nextDate: '01.12.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '01.12.2025',
      nextDate: '01.06.2026',
      certNumber: '—',
      certExpiry: '—',
    },
    internship: { status: 'done', mentor: '—', endDate: '—', admitted: true },
    sout: { class: '2', date: '14.01.2024', nextDate: '14.01.2026', status: 'ok' },
    riskAssessment: { level: 'Средний', score: 6, status: 'warn', plan: '1 мероприятие в плане' },
    ppe: { count: 4, autoAssigned: true, items: ['Очки защитные', 'Перчатки х/б', 'Ботинки', 'Костюм'] },
  },
  {
    id: 8,
    name: 'Морозова Татьяна Игоревна',
    initials: 'МТ',
    position: 'Бухгалтер',
    department: 'Бухгалтерия',
    unit: 'Расчётный отдел',
    tabNumber: '00153',
    workplaceId: 'РМ-105',
    workConditionClass: '1',
    active: true,
    hireDate: '22.01.2016',
    medical: { status: 'ok', lastDate: '10.01.2026', nextDate: '10.01.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '10.01.2026',
      nextDate: '10.07.2026',
      certNumber: '—',
      certExpiry: '—',
    },
    internship: { status: 'done', mentor: '—', endDate: '—', admitted: true },
    sout: { class: '1', date: '05.04.2023', nextDate: '05.04.2026', status: 'ok' },
    riskAssessment: { level: 'Низкий', score: 1, status: 'ok', plan: 'Нет активных мероприятий' },
    ppe: { count: 1, autoAssigned: true, items: ['—'] },
  },
  {
    id: 9,
    name: 'Баранов Геннадий Викторович',
    initials: 'БГ',
    position: 'Мастер смены',
    department: 'Цех №2',
    unit: 'Участок №2',
    tabNumber: '00201',
    workplaceId: 'РМ-060',
    workConditionClass: '2',
    active: true,
    hireDate: '14.09.2014',
    medical: { status: 'ok', lastDate: '15.01.2026', nextDate: '15.01.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '15.01.2026',
      nextDate: '15.07.2026',
      certNumber: 'ОТ-2026-0341',
      certExpiry: '15.01.2026',
    },
    internship: { status: 'done', mentor: '—', endDate: '—', admitted: true },
    sout: { class: '2', date: '20.05.2023', nextDate: '20.05.2026', status: 'ok' },
    riskAssessment: { level: 'Низкий', score: 2, status: 'ok', plan: 'Нет активных мероприятий' },
    ppe: { count: 3, autoAssigned: true, items: ['Каска', 'Перчатки', 'Жилет'] },
  },
  {
    id: 10,
    name: 'Алексеев Константин Сергеевич',
    initials: 'АК',
    position: 'Слесарь-ремонтник 4 разр.',
    department: 'Цех №1',
    unit: 'Участок №1',
    tabNumber: '00556',
    workplaceId: 'РМ-048',
    workConditionClass: '3.1',
    active: true,
    hireDate: '27.05.2022',
    medical: { status: 'ok', lastDate: '01.04.2026', nextDate: '01.04.2026', result: 'Допущен' },
    training: {
      briefingStatus: 'ok',
      lastDate: '01.04.2026',
      nextDate: '01.10.2026',
      certNumber: '—',
      certExpiry: '—',
    },
    internship: { status: 'done', mentor: 'Баринов К.Л.', endDate: '15.02.2025', admitted: true },
    sout: { class: '3.1', date: '10.06.2022', nextDate: '10.06.2026', status: 'ok' },
    riskAssessment: { level: 'Средний', score: 7, status: 'warn', plan: '1 мероприятие в работе' },
    ppe: { count: 5, autoAssigned: true, items: ['Каска', 'Перчатки х/б', 'Ботинки', 'Очки', 'Костюм'] },
  },
];
